/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import {
  Alert, Col, Label,
} from 'reactstrap';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import {
  Formik, Field, Form, ErrorMessage,
} from 'formik';
import * as Yup from 'yup';
import { isNull } from 'lodash';
import { post } from '../../../helpers/api_helper';

function ShipMethodMappingForm(props) {
  const {
    entity,
    t,
    onSuccess,
  } = props;

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
  };

  const save = async (values) => {
    try {
      setLoading(true);
      setErrorMessage(null);

      let result;
      if (entity.id === undefined) {
        result = await post('/api/orders/shipping/mappings/create', values);
      } else {
        result = await post(`/api/orders/shipping/mappings/save/${entity.id}`, values);
      }

      toastr.success(t(`Item ID ${result.id} saved`), t('Success'));
      onSuccess(result);
    } catch (error) {
      setErrorMessage(`${error.message}: ${error.response?.data || ''}`);
    } finally {
      setLoading(false);
    }
  };

  const validationSchema = Yup.object().shape({
    preferred_methods_text: Yup.string().trim().required(t('At least one preferred method is required')),
    ship_method_code: Yup.string().trim().required(t('Ship method code is required')),
    carrier: Yup.string().nullable(),
    service_name: Yup.string().nullable(),
    account_number: Yup.string().trim().max(100).nullable(),
    reference_match: Yup.string()
      .nullable()
      .test('valid-regex', t('Reference match must be a valid regex pattern'), (value) => {
        if (value == null || value.trim() === '') {
          return true;
        }

        try {
          RegExp(value);
          return true;
        } catch (error) {
          return false;
        }
      }),
    max_weight: Yup.number()
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .min(0, t('Max weight must be zero or greater')),
    notes: Yup.string().nullable(),
    is_active: Yup.boolean(),
  });

  let em = (
    <Alert color="danger" className="mt-3" isOpen={!isNull(errorMessage)}>
      {errorMessage}
    </Alert>
  );

  if (errorMessage != null) {
    const match = errorMessage.match(/(.*)<<<(.+)>>>(.*)/);

    if (match !== null) {
      em = (
        <Alert color="danger" className="mt-3" isOpen>
          {match[1]}
          <strong>{match[2]}</strong>
          {match[3]}
        </Alert>
      );
    }
  }

  return (
    <>
      {em}
      <Formik
        enableReinitialize
        initialValues={entity}
        validationSchema={validationSchema}
        onSubmit={save}
      >
        {({ errors }) => (
          <Form className="mt-3" id="ShipMethodMappingForm" autoComplete="off">
            {Object.entries(errors).length > 0 && (
              <Alert color="danger">{t('Form contains errors')}</Alert>
            )}

            <div className="row mt-4 mb-4">
              <Label for="preferred_methods_text" className="col-sm-3 col-form-label">
                {t('Preferred Methods')}
              </Label>
              <Col sm={9}>
                <Field
                  as="textarea"
                  rows="5"
                  className="form-control"
                  id="preferred_methods_text"
                  name="preferred_methods_text"
                />
                <small className="text-muted">
                  {t('Enter one match string per line.')}
                </small>
                <ErrorMessage name="preferred_methods_text" component="div" className="text-danger" />
              </Col>
            </div>

            <div className="row mt-4 mb-4">
              <Label for="ship_method_code" className="col-sm-3 col-form-label">
                {t('Ship Method Code')}
              </Label>
              <Col sm={9}>
                <Field className="form-control" id="ship_method_code" name="ship_method_code" />
                <small className="text-muted">
                  {t('Use AUTO_SELECT_CHEAPEST to rate shop and auto-ship the cheapest service for matching preferred methods.')}
                </small>
                <ErrorMessage name="ship_method_code" component="div" className="text-danger" />
              </Col>
            </div>

            <div className="row mt-4 mb-4">
              <Label for="carrier" className="col-sm-3 col-form-label">
                {t('Carrier')}
              </Label>
              <Col sm={9}>
                <Field className="form-control" id="carrier" name="carrier" />
                <ErrorMessage name="carrier" component="div" className="text-danger" />
              </Col>
            </div>

            <div className="row mt-4 mb-4">
              <Label for="service_name" className="col-sm-3 col-form-label">
                {t('Service Name')}
              </Label>
              <Col sm={9}>
                <Field className="form-control" id="service_name" name="service_name" />
                <ErrorMessage name="service_name" component="div" className="text-danger" />
              </Col>
            </div>

            <div className="row mt-4 mb-4">
              <Label for="account_number" className="col-sm-3 col-form-label">
                {t('Shipping Account Number')}
              </Label>
              <Col sm={9}>
                <Field className="form-control" id="account_number" name="account_number" />
                <small className="text-muted">
                  {t('Optional base carrier account override for this method. This is not third-party billing.')}
                </small>
                <ErrorMessage name="account_number" component="div" className="text-danger" />
              </Col>
            </div>

            <div className="row mt-4 mb-4">
              <Label for="reference_match" className="col-sm-3 col-form-label">
                {t('Reference Match')}
              </Label>
              <Col sm={9}>
                <Field className="form-control" id="reference_match" name="reference_match" />
                <small className="text-muted">
                  {t('Optional regex pattern to match order.reference before this mapping applies.')}
                </small>
                <ErrorMessage name="reference_match" component="div" className="text-danger" />
              </Col>
            </div>

            <div className="row mt-4 mb-4">
              <Label for="max_weight" className="col-sm-3 col-form-label">
                {t('Max Weight')}
              </Label>
              <Col sm={9}>
                <Field className="form-control" id="max_weight" name="max_weight" type="number" step="0.01" min="0" />
                <small className="text-muted">
                  {t('Leave blank for no weight limit.')}
                </small>
                <ErrorMessage name="max_weight" component="div" className="text-danger" />
              </Col>
            </div>

            <div className="row mt-4 mb-4">
              <Label for="notes" className="col-sm-3 col-form-label">
                {t('Notes')}
              </Label>
              <Col sm={9}>
                <Field
                  as="textarea"
                  rows="4"
                  className="form-control"
                  id="notes"
                  name="notes"
                />
                <ErrorMessage name="notes" component="div" className="text-danger" />
              </Col>
            </div>

            <div className="row mt-4 mb-2">
              <Label for="is_active" className="col-sm-3 col-form-label">
                {t('Active')}
              </Label>
              <Col sm={9} className="d-flex align-items-center">
                <div className="form-check form-switch mb-0">
                  <Field className="form-check-input" type="checkbox" id="is_active" name="is_active" />
                </div>
              </Col>
            </div>

            {loading && (
              <Alert color="info" className="mt-3 mb-0">
                {t('Saving...')}
              </Alert>
            )}
          </Form>
        )}
      </Formik>
    </>
  );
}

ShipMethodMappingForm.defaultProps = {
  t: (x) => x,
  entity: {
    preferred_methods: [],
    preferred_methods_text: '',
    ship_method_code: '',
    carrier: '',
    service_name: '',
    account_number: '',
    reference_match: '',
    max_weight: '',
    notes: '',
    is_active: true,
  },
  onSuccess: () => {},
};

ShipMethodMappingForm.propTypes = {
  t: PropTypes.any,
  entity: PropTypes.object,
  onSuccess: PropTypes.func,
};

export default connect(() => ({}), {})(withTranslation()(ShipMethodMappingForm));
